import { useState, useEffect } from 'react';
import { Bell, BellOff, Check, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { initializeOneSignal, subscribeUser, getSubscriptionStatus } from '@/lib/oneSignal';
import { supabase } from '@/integrations/supabase/client';

export const NotificationManager = () => {
  const [status, setStatus] = useState<'loading' | 'supported' | 'unsupported'>('loading');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const initialize = async () => {
      try {
        // Check basic browser support first
        if (!('Notification' in window) && !('serviceWorker' in navigator)) {
          setStatus('unsupported');
          return;
        }

        // Try to initialize OneSignal with timeout
        const initPromise = initializeOneSignal();
        const timeoutPromise = new Promise<boolean>((resolve) => 
          setTimeout(() => resolve(false), 5000)
        );
        
        const oneSignalReady = await Promise.race([initPromise, timeoutPromise]);
        
        if (oneSignalReady) {
          // Check current subscription status
          const subscriptionStatus = await getSubscriptionStatus();
          setIsSubscribed(subscriptionStatus.isSubscribed);
          setPlayerId(subscriptionStatus.playerId);
          
          // Store player ID if we have one and user is logged in
          if (subscriptionStatus.playerId) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              await supabase
                .from('profiles')
                .update({ 
                  onesignal_player_id: subscriptionStatus.playerId,
                  notifications_enabled: true
                })
                .eq('id', user.id);
            }
          }
        }
        
        setStatus('supported');
      } catch (error) {
        console.error('Error initializing notifications:', error);
        setStatus('supported'); // Still mark as supported to allow trying to enable
      }
    };

    initialize();
  }, []);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      console.log('Starting notification setup process...');
      
      // First try OneSignal
      const result = await subscribeUser();
      console.log('Subscription result:', result);
      
      if (result.isSubscribed && result.playerId) {
        console.log('OneSignal subscription successful');
        setIsSubscribed(true);
        setPlayerId(result.playerId);
        
        // Store the player ID for this user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('Updating user profile with player ID...');
          await supabase
            .from('profiles')
            .update({ 
              onesignal_player_id: result.playerId,
              notifications_enabled: true
            })
            .eq('id', user.id);
        }
        
        toast({
          title: "Push Notifications Enabled!",
          description: "You'll receive order updates and promotions.",
        });
        
        // Send test notification via OneSignal
        console.log('Sending test notification...');
        await supabase.functions.invoke('send-push-notification', {
          body: {
            message: 'Push notifications are now working! You\'ll receive updates about your orders.',
            heading: 'GAMBGO Notifications Active',
            userIds: [result.playerId]
          }
        });
        
      } else {
        // OneSignal subscription failed
        console.log('OneSignal subscription failed:', result);
        
        // Check if it's a domain error
        if (result.error?.includes('Domain not configured')) {
          toast({
            title: "Domain Configuration Required",
            description: "Please add this domain to OneSignal dashboard under Settings > Allowed Origins",
            variant: "destructive",
          });
        } else {
          throw new Error(result.error || 'Failed to enable push notifications');
        }
      }
      
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      
      toast({
        title: "Setup Failed",
        description: errorMsg.includes('Domain not configured') 
          ? "This domain needs to be added to OneSignal dashboard settings" 
          : errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (status === 'loading') {
      return {
        icon: <Loader className="h-5 w-5 animate-spin text-gray-600" />,
        text: 'Checking notification support...',
        color: 'text-gray-600'
      };
    }
    
    if (status === 'unsupported') {
      return {
        icon: <BellOff className="h-5 w-5 text-red-600" />,
        text: 'Notifications not supported in this browser',
        color: 'text-red-600'
      };
    }
    
    if (isSubscribed && playerId) {
      return {
        icon: <Check className="h-5 w-5 text-green-600" />,
        text: 'Push notifications enabled',
        color: 'text-green-600'
      };
    }
    
    return {
      icon: <Bell className="h-5 w-5 text-gray-600" />,
      text: 'Enable notifications for order updates',
      color: 'text-gray-600'
    };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="h-5 w-5 mr-2" />
          Push Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          {statusDisplay.icon}
          <span className={`text-sm ${statusDisplay.color}`}>{statusDisplay.text}</span>
        </div>

        {playerId && (
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-md">
            Device ID: {playerId.slice(0, 8)}...
          </div>
        )}

        {status === 'supported' && !isSubscribed && (
          <Button 
            onClick={handleEnableNotifications}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              'Enable Push Notifications'
            )}
          </Button>
        )}

        {isSubscribed && playerId && (
          <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md">
            ✓ You're all set! You'll receive notifications for:
            <ul className="mt-2 ml-4 list-disc">
              <li>Order status updates</li>
              <li>Driver assignments</li>
              <li>Delivery notifications</li>
              <li>Special promotions</li>
            </ul>
          </div>
        )}

        {status === 'unsupported' && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Safari.
          </div>
        )}
      </CardContent>
    </Card>
  );
};